/* Timbrica — /boarding-pass: интерфейс.
 *
 * Разбор живёт в bcbp.js, дневник — в journal.js. Здесь только три входа
 * (камера, картинка, вставленная строка), объяснение полей и панель дневника.
 *
 * ⚠️ ПОРЯДОК ОТВЕТА — ЧАСТЬ ОТВЕТА.
 * Человек приходит сюда с одним из двух вопросов: «что вообще написано на моём
 * талоне» и «что значит вот эта буква». Поэтому сверху идёт короткая карточка
 * рейса (маршрут, место, время), а разбор поля за полем — ниже, и КАЖДОЕ поле
 * объяснено словами, а не названо термином из спецификации. Талон, в котором
 * стоит SSSS или PreCheck, поднимает это наверх: ради этих двух строк тул и
 * ищут в поиске.
 */
import { parse, PASSENGER_STATUS, SELECTEE } from './bcbp.js';
import * as journal from './journal.js';

const CFG = window.BOARDING_PASS_CFG || {};
const T = CFG.t || {};
const AIRLINES = CFG.airlines || {};
const LOCALE = CFG.locale || 'en';

const tr = (k) => (T[k] != null ? T[k] : k);
const $ = (id) => document.getElementById(id);
const ae = (ev, p) => { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (e) {} };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

let airportsIndex = null;
let lastParsed = null;
let cam = null;
let scanning = false;

const isPro = () => (window.__isProNow ? window.__isProNow(!!CFG.isPro) : !!CFG.isPro);

// ── Аэропорты: тот же общий датасет, что и у калькулятора компенсаций ───────
async function airports() {
  if (airportsIndex) return airportsIndex;
  const r = await fetch(CFG.airportsUrl, { credentials: 'omit' });
  const j = await r.json();
  const map = new Map();
  for (const row of j.rows) {
    map.set(row[0], {
      iata: row[0], icao: row[1], name: row[2], city: row[3],
      cc: j.cc[row[4]], lat: row[5], lon: row[6],
      tz: row[7] >= 0 ? j.tz[row[7]] : null,
    });
  }
  airportsIndex = map;
  return map;
}

function ap(code) {
  return airportsIndex ? (airportsIndex.get(String(code || '').toUpperCase()) || null) : null;
}

function airportLabel(code) {
  const a = ap(code);
  if (!a) return code || '—';
  const city = (a.city || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  return city ? city + ' (' + a.iata + ')' : a.name + ' (' + a.iata + ')';
}

function carrierLabel(code) {
  const row = AIRLINES[String(code || '').toUpperCase()];
  return row ? row[0] : (code || '—');
}

function countryName(cc) {
  try { return new Intl.DisplayNames([LOCALE], { type: 'region' }).of(cc) || cc; }
  catch (e) { return cc; }
}

// ── Разбор и вывод ──────────────────────────────────────────────────────────
async function handleString(text, source) {
  const parsed = parse(text);
  lastParsed = parsed;
  ae('bp_decoded', { ok: parsed.ok, legs: parsed.legs.length, source: source || 'unknown' });

  if (!parsed.ok) {
    showError(parsed);
    return;
  }
  await airports().catch(() => {});
  render(parsed);
  stage('decoded');
}

function showError(parsed) {
  const out = $('bp-result');
  const why = parsed.warnings.includes('not_bcbp') ? tr('err_not_boarding_pass')
    : parsed.warnings.includes('too_short') ? tr('err_too_short')
    : parsed.warnings.includes('legacy_format') ? tr('err_legacy')
    : tr('err_unreadable');
  out.innerHTML = '<div class="bp-empty bp-error">' + esc(why) + '</div>';
}

function render(p) {
  const out = $('bp-result');
  const parts = [];

  // ── Сигнальные строки поднимаются наверх ─────────────────────────────────
  const flags = [];
  for (const leg of p.legs) {
    if (leg.selectee === 'selectee_ssss') flags.push(['ssss', tr('flag_ssss')]);
    if (leg.selectee === 'tsa_precheck') flags.push(['pre', tr('flag_precheck')]);
    if (leg.status === 'standby') flags.push(['standby', tr('flag_standby')]);
  }
  for (const [kind, text] of flags) {
    parts.push('<div class="bp-flag bp-flag-' + kind + '">' + esc(text) + '</div>');
  }

  // ── Пассажир ─────────────────────────────────────────────────────────────
  const nm = p.name;
  parts.push(
    '<div class="bp-pax"><span class="bp-pax-name">' +
    esc([nm.first, nm.last].filter(Boolean).join(' ') || nm.raw) + '</span>' +
    (nm.title ? '<span class="bp-pax-title">' + esc(nm.title) + '</span>' : '') +
    (p.eticket ? '<span class="bp-chip">' + esc(tr('eticket')) + '</span>' : '') +
    '</div>'
  );

  // ── Плечи ────────────────────────────────────────────────────────────────
  p.legs.forEach((leg) => {
    const a = ap(leg.from);
    const b = ap(leg.to);
    parts.push(
      '<section class="bp-leg">' +
      '<header class="bp-leg-head">' +
      '<div class="bp-route"><span>' + esc(airportLabel(leg.from)) + '</span>' +
      '<span class="bp-arrow" aria-hidden="true">→</span>' +
      '<span>' + esc(airportLabel(leg.to)) + '</span></div>' +
      '<div class="bp-leg-sub">' + esc(carrierLabel(leg.carrier)) + ' ' +
      esc(leg.carrier + (leg.flightNumber || '')) +
      (leg.date ? ' · ' + esc(leg.date) + (leg.dateApproximate ? ' ' + esc(tr('date_approx')) : '') : '') +
      '</div></header>' +

      '<div class="bp-facts">' +
      fact(tr('f_seat'), leg.seat || '—', tr('f_seat_note')) +
      fact(tr('f_class'), compartmentLabel(leg.compartment), tr('f_class_note')) +
      fact(tr('f_pnr'), leg.pnr || '—', tr('f_pnr_note')) +
      fact(tr('f_sequence'), Number.isFinite(leg.sequenceNumber) ? String(leg.sequenceNumber) : '—',
        sequenceNote(leg.sequenceNumber)) +
      fact(tr('f_status'), tr('status_' + leg.status), tr('f_status_note')) +
      (leg.checkinSource ? fact(tr('f_checkin'), tr('checkin_' + leg.checkinSource), tr('f_checkin_note')) : '') +
      (leg.ffNumber ? fact(tr('f_ff'), leg.ffNumber + (leg.ffAirline ? ' (' + leg.ffAirline + ')' : ''), tr('f_ff_note')) : '') +
      (leg.freeBaggage ? fact(tr('f_baggage'), leg.freeBaggage, tr('f_baggage_note')) : '') +
      (leg.baggageTags ? fact(tr('f_bagtags'), leg.baggageTags.join(', '), tr('f_bagtags_note')) : '') +
      (leg.selecteeCode ? fact(tr('f_selectee'), tr('selectee_' + leg.selectee), tr('f_selectee_note')) : '') +
      (leg.marketingCarrier && leg.marketingCarrier !== leg.carrier
        ? fact(tr('f_marketing'), carrierLabel(leg.marketingCarrier), tr('f_marketing_note')) : '') +
      '</div>' +

      (a && b ? '<p class="bp-dist">' + esc(tr('distance').replace(':km', String(Math.round(gc(a, b))))) +
        (a.cc !== b.cc ? ' · ' + esc(countryName(a.cc)) + ' → ' + esc(countryName(b.cc)) : '') + '</p>' : '') +
      '</section>'
    );
  });

  if (p.security && p.security.present) {
    parts.push('<p class="bp-note">' + esc(tr('security_present')) + '</p>');
  }

  parts.push('<div class="tc-actions">' +
    '<button type="button" class="tc-btn" id="bp-add">' + esc(tr('add_to_journal')) + '</button>' +
    '<button type="button" class="tc-btn ghost sm" id="bp-raw-toggle">' + esc(tr('show_raw')) + '</button>' +
    '</div>' +
    '<pre class="bp-raw" id="bp-raw" hidden>' + esc(p.raw) + '</pre>');

  out.innerHTML = parts.join('');

  const addBtn = $('bp-add');
  if (addBtn) addBtn.addEventListener('click', onAddToJournal);
  const rawBtn = $('bp-raw-toggle');
  if (rawBtn) rawBtn.addEventListener('click', () => { const el = $('bp-raw'); el.hidden = !el.hidden; });
}

function fact(label, value, note) {
  return '<div class="bp-fact"><dt>' + esc(label) + '</dt><dd>' + esc(value) + '</dd>' +
    (note ? '<p>' + esc(note) + '</p>' : '') + '</div>';
}

function compartmentLabel(c) {
  if (!c) return '—';
  const key = 'class_' + c;
  const v = tr(key);
  return v === key ? c : c + ' — ' + v;
}

/**
 * Порядковый номер регистрации — единственное поле талона, у которого есть
 * практический смысл прямо сейчас: при овербукинге снимают с рейса тех, кто
 * зарегистрировался последним. Число без этого пояснения бесполезно.
 */
function sequenceNote(n) {
  if (!Number.isFinite(n)) return tr('f_sequence_note');
  if (n <= 15) return tr('f_sequence_early');
  return tr('f_sequence_note');
}

function gc(a, b) {
  const R = 6371.0088, rad = Math.PI / 180;
  const p1 = a.lat * rad, p2 = b.lat * rad;
  const dp = (b.lat - a.lat) * rad, dl = (b.lon - a.lon) * rad;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ── Дневник ─────────────────────────────────────────────────────────────────
function onAddToJournal() {
  if (!isPro()) { showJournalUpsell(true); return; }
  const res = journal.addFromParse(lastParsed, { at: Date.now() });
  ae('bp_journal_add', res);
  const msg = res.added
    ? tr('journal_added').replace(':n', String(res.added))
    : tr('journal_all_duplicates');
  window.__toast && window.__toast(msg, res.added ? 'success' : 'info');
  renderJournal();
}

function renderJournal() {
  const box = $('bp-journal');
  if (!box) return;

  if (!isPro()) { showJournalUpsell(false); return; }

  const s = journal.stats(ap);
  if (!s.flights) {
    box.innerHTML = '<p class="bp-empty">' + esc(tr('journal_empty')) + '</p>' + journalTools();
    wireJournalTools();
    return;
  }

  const rows = journal.list().slice().reverse().map((f) =>
    '<tr><td>' + esc(f.date || '—') + '</td><td>' + esc(f.from + ' → ' + f.to) + '</td>' +
    '<td>' + esc((f.carrier || '') + (f.flightNumber || '')) + '</td>' +
    '<td>' + esc(f.seat || '') + '</td>' +
    '<td><button type="button" class="bp-del" data-key="' + esc(f.key) + '" aria-label="' +
    esc(tr('journal_remove')) + '">×</button></td></tr>'
  ).join('');

  box.innerHTML =
    '<div class="tc-stats">' +
    stat(String(s.flights), tr('st_flights')) +
    stat(new Intl.NumberFormat(LOCALE).format(s.km) + ' ' + tr('km'), tr('st_km')) +
    stat(s.aroundEarth.toFixed(1) + '×', tr('st_around')) +
    stat(String(s.countries), tr('st_countries')) +
    stat(String(s.airports), tr('st_airports')) +
    '</div>' +
    (s.skipped ? '<p class="bp-note">' + esc(tr('journal_skipped').replace(':n', String(s.skipped))) + '</p>' : '') +
    '<div class="tc-tablewrap"><table class="tc-table"><tbody>' + rows + '</tbody></table></div>' +
    journalTools();

  box.querySelectorAll('.bp-del').forEach((b) => {
    b.addEventListener('click', () => { journal.remove(b.dataset.key); renderJournal(); });
  });
  wireJournalTools();
}

function stat(v, label) {
  return '<div class="tc-stat"><div class="tc-stat-v">' + esc(v) + '</div>' +
    '<div class="tc-stat-l">' + esc(label) + '</div></div>';
}

function journalTools() {
  return '<div class="tc-actions">' +
    '<button type="button" class="tc-btn ghost sm" id="bp-export">' + esc(tr('journal_export')) + '</button>' +
    '<label class="tc-btn ghost sm bp-import">' + esc(tr('journal_import')) +
    '<input type="file" id="bp-import" accept="application/json,.json" hidden></label>' +
    '</div>';
}

function wireJournalTools() {
  const ex = $('bp-export');
  if (ex) ex.addEventListener('click', () => {
    const blob = new Blob([journal.exportJson()], { type: 'application/json' });
    window.__downloadFile && window.__downloadFile(blob, 'timbrica-flights.json');
    ae('bp_journal_export', { n: journal.count() });
  });
  const im = $('bp-import');
  if (im) im.addEventListener('change', async () => {
    const f = im.files && im.files[0];
    if (!f) return;
    const res = journal.importJson(await f.text());
    window.__toast && window.__toast(
      res.ok ? tr('journal_imported').replace(':n', String(res.added)) : tr('journal_import_failed'),
      res.ok ? 'success' : 'error');
    im.value = '';
    renderJournal();
  });
}

/**
 * Не-Премиум ВИДИТ, что дневник есть, и на демонстрационных данных — что он
 * даёт. Прятать существование функции значит не продать её, а спрятать.
 */
function showJournalUpsell(fromClick) {
  const box = $('bp-journal');
  if (!box) return;
  box.innerHTML =
    '<div class="bp-locked">' +
    '<div class="tc-stats bp-stats-demo" aria-hidden="true">' +
    stat('37', tr('st_flights')) + stat('184 502 ' + tr('km'), tr('st_km')) +
    stat('4.6×', tr('st_around')) + stat('19', tr('st_countries')) + stat('28', tr('st_airports')) +
    '</div>' +
    '<p class="bp-locked-text">' + esc(tr('journal_locked')) + '</p>' +
    '<a class="tc-btn" href="' + esc(CFG.proUrl || '#') + '">' + esc(tr('journal_unlock')) + '</a>' +
    '<p class="bp-note">' + esc(tr('journal_export_always')) + '</p>' +
    '</div>';
  if (fromClick) ae('bp_journal_upsell', { from: 'add_button' });
}

// ── Входы ───────────────────────────────────────────────────────────────────
async function decodeImage(file) {
  if (!window.__qrDecode) { window.__toast && window.__toast(tr('err_no_decoder'), 'error'); return; }
  stage('decoding');
  try {
    const hits = await window.__qrDecode.fromFile(file, { formats: ['PDF417', 'AZTEC', 'QR_CODE', 'DATA_MATRIX'] });
    const text = hits && hits.length ? (hits[0].text || hits[0].raw || '') : '';
    if (!text) {
      $('bp-result').innerHTML = '<div class="bp-empty bp-error">' + esc(tr('err_no_barcode')) + '</div>';
      ae('bp_no_barcode', { size: file.size });
      return;
    }
    await handleString(text, 'image');
  } catch (e) {
    // The shared decoder names a reader that never started (code ENGINE,
    // qr-decode.js since 26.09) — that is this sentence, not an engine string.
    if (e && e.code === 'ENGINE') { window.__toast && window.__toast(tr('err_no_decoder'), 'error'); return; }
    window.__handleError ? window.__handleError(e, 'boarding-pass/image') : null;
  }
}

async function startCamera() {
  if (scanning) { stopCamera(); return; }
  if (!window.CameraManager || !window.__qrDecode) {
    window.__toast && window.__toast(tr('err_no_camera_lib'), 'error');
    return;
  }
  const video = $('bp-video');
  const wrap = $('bp-camera');
  try {
    cam = cam || new window.CameraManager({ slug: 'boarding-pass' });
    const stream = await cam.start({ facingMode: 'environment', width: 1920, height: 1080 });
    video.srcObject = stream;
    // play() НЕ ждём: на потоке, который не отдаёт кадров, её обещание не
    // разрешается никогда (readyState 0 — замер 2026-10-08), и сторож «кадры не
    // пошли» в loopScan, написанный ровно для этого случая, не запускался вовсе.
    const played = video.play();
    if (played && played.catch) played.catch(() => {});
    wrap.hidden = false;
    scanning = true;
    $('bp-cam-btn').textContent = tr('camera_stop');
    ae('bp_camera_start', {});
    loopScan(video);
  } catch (err) {
    // CameraManager классифицирует отказ и даёт готовую фразу — показываем её
    // ТАМ, куда смотрят, а не только тостом.
    const msg = (window.CameraManager && cam && cam.getErrorMessage)
      ? cam.getErrorMessage(err) : tr('err_camera');
    wrap.hidden = false;
    $('bp-cam-status').textContent = msg;
    ae('bp_camera_fail', { code: (err && err.code) || 'unknown' });
  }
}

function stopCamera() {
  scanning = false;
  try { cam && cam.stop(); } catch (e) {}
  const v = $('bp-video');
  if (v) v.srcObject = null;
  $('bp-camera').hidden = true;
  $('bp-cam-btn').textContent = tr('camera_start');
}

function loopScan(video) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  let firstFrameSeen = false;
  let noFramesSaid = false;
  const started = Date.now();
  const hint = $('bp-cam-status').textContent;

  const tick = async () => {
    if (!scanning) return;
    if (video.videoWidth) {
      // Поздний кадр: живая камера бывает медленнее срока — подсказка возвращается.
      if (!firstFrameSeen && noFramesSaid) { $('bp-cam-status').textContent = hint; ae('bp_camera_frames_late', {}); }
      firstFrameSeen = true;
      if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
      if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
      // Через общий кадр камеры: система может отнять кадр между проверкой и
      // отрисовкой (iOS: переключатель приложений, звонок), и голый drawImage
      // бросал InvalidStateError ДО requestAnimationFrame ниже — цикл умирал,
      // камера горела под «сканирую». Целый кадр в родных пикселях здесь
      // намеренно: посадочный PDF417 широкий и плотный (а не область прицела,
      // как у остальных сканеров — __qrDecode.liveFrame).
      if (!window.CameraManager.drawFrame(ctx, video, 0, 0)) { requestAnimationFrame(tick); return; }
      try {
        const hits = await window.__qrDecode.fromCanvasFast(canvas, { formats: ['PDF417', 'AZTEC', 'QR_CODE'] });
        if (hits && hits.length) {
          const text = hits[0].text || hits[0].raw || '';
          if (text) {
            stopCamera();
            await handleString(text, 'camera');
            return;
          }
        }
      } catch (e) { /* кадр не читается — берём следующий */ }
    } else if (!firstFrameSeen && !noFramesSaid && Date.now() - started > 6000) {
      // ⚠️ start() зарезолвился ≠ кадры пошли. Без этого сторожа человек
      // смотрит в чёрный прямоугольник под надписью «сканирую». ОДИН раз: цикл
      // идёт на каждом кадре экрана, и без флага событие уходило бы десятки раз
      // в секунду, пока камера молчит (до 08.10 сюда не доходили вовсе — цикл
      // стартовал после await play(), которое без кадров не разрешается).
      noFramesSaid = true;
      $('bp-cam-status').textContent = tr('err_camera_no_frames');
      ae('bp_camera_no_frames', {});
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// ── Старт ───────────────────────────────────────────────────────────────────
function init() {
  const camBtn = $('bp-cam-btn');
  if (camBtn) camBtn.addEventListener('click', startCamera);

  // ⚠️ Через ОБЩИЙ байндер, а не сырым addEventListener на документ.
  // Модуль выполняется отложенно, а зона может выдать файл раньше: примитив
  // проигрывает такому опоздавшему слушателю уже выбранный файл, сырой
  // слушатель этот файл теряет молча. Сторож — UploaderListenerRaceTest.
  const zone = document.getElementById('bpUpload') || document;
  if (typeof window.__bindUploaderFile === 'function') {
    window.__bindUploaderFile(zone, (file) => { if (file) decodeImage(file); });
  } else {
    zone.addEventListener('tool-uploader:file', (e) => {
      const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
      if (f) decodeImage(f);
    });
  }

  const pasteBtn = $('bp-paste-btn');
  if (pasteBtn) pasteBtn.addEventListener('click', () => {
    const v = ($('bp-paste') || {}).value || '';
    if (v.trim()) handleString(v.trim(), 'paste');
  });

  // Камеру гасим, когда вкладка уходит: держать её включённой в фоне нельзя.
  document.addEventListener('visibilitychange', () => { if (document.hidden && scanning) stopCamera(); });
  window.addEventListener('pagehide', () => { if (scanning) stopCamera(); });

  renderJournal();
  airports().catch(() => {});
  stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
